<script lang="ts">
  import { onDestroy, onMount, untrack } from 'svelte';
  import Badge from '../lib/ui/Badge.svelte';
  import Button from '../lib/ui/Button.svelte';
  import Chip from '../lib/ui/Chip.svelte';
  import Notice from '../lib/ui/Notice.svelte';
  import Pager from '../lib/ui/Pager.svelte';
  import { ReportSendBadge } from '../lib/report-send-badge';
  import { ReportSendDeliveries } from '../lib/report-send-deliveries.svelte';
  import { ReportSendError } from '../lib/report-send-error';
  import { ReportSendOverview } from '../lib/report-send-overview';
  import { formatDateTime } from '../lib/time';
  import type { ReportDefinitionSend, ReportDefinitionSummary } from '../lib/api';
  import type { ReportDefinitionsBoard } from '../lib/report-definitions-board.svelte';

  interface Props {
    board: ReportDefinitionsBoard;
    definition: ReportDefinitionSummary;
    send: ReportDefinitionSend;
  }

  let { board, definition, send }: Props = $props();

  const loader = untrack(() => new ReportSendDeliveries({ board, definition, send }));
  const page = $derived(loader.page);
  const hasFailures = $derived(new ReportSendOverview(send).hasFailures);
  const noun = $derived(loader.onlyFailed ? 'contatos com falha' : 'contatos');

  onMount(() => void loader.load());
  onDestroy(() => loader.abandon());
</script>

{#if hasFailures || page}
  <div class="filters">
    {#if hasFailures}<Chip pressed={loader.onlyFailed} onclick={() => void loader.toggleOnlyFailed()}>Só falhas</Chip>{/if}
    {#if page}<span class="count">{page.total} {noun}</span>{/if}
  </div>
{/if}
{#if loader.failedBeforeAnyPage}
  <div class="empty">
    <p>Não deu para carregar os contatos.</p>
    <Button size="sm" onclick={() => void loader.load(loader.failedPage ?? 1)}>Tentar de novo</Button>
  </div>
{:else if page === null}
  <p class="empty">Carregando os contatos…</p>
{:else if page.total === 0}
  <p class="empty">{loader.onlyFailed ? 'Nenhum contato com falha neste envio.' : 'Nenhum contato registrado neste envio.'}</p>
{:else}
  {#if loader.failedPage}
    <div class="page-failure">
      <Notice tone="danger">
        Não deu para carregar a página {loader.failedPage}.
        <Button size="sm" onclick={() => void loader.load(loader.failedPage ?? 1)}>Tentar de novo</Button>
      </Notice>
    </div>
  {/if}
  <table>
    <thead>
      <tr><th>Contato</th><th>WhatsApp</th><th>E-mail</th></tr>
    </thead>
    <tbody>
      {#each page.items as delivery (delivery.hqCustomerId ?? delivery.recipientEmail)}
        <tr>
          <td class="name">{ReportSendOverview.recipientOf(delivery)}</td>
          {#each [delivery.whatsapp, delivery.email] as outcome, channel (channel)}
            <td>
              {#if outcome}
                {@const badge = ReportSendBadge.of(outcome)}
                <span class="state"><Badge tone={badge.tone}>{badge.label}</Badge><span class="sub mono">{formatDateTime(outcome.at)}</span></span>
                {#if outcome.error}<span class="error">{ReportSendError.label(outcome.error)}</span>{/if}
              {:else}
                <span class="sub">—</span>
              {/if}
            </td>
          {/each}
        </tr>
      {/each}
    </tbody>
  </table>
  {#if page.total > page.perPage}
    <Pager page={page.page} total={page.total} perPage={page.perPage} {noun} label="Páginas de contatos" onGo={(next) => void loader.load(next)} />
  {/if}
{/if}

<style>
  .filters {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    padding: 0 var(--space-4) var(--space-3);
  }
  table {
    width: 100%;
    border-collapse: collapse;
  }
  th {
    text-align: left;
    font-size: 12px;
    font-weight: 500;
    color: var(--text-secondary);
    text-transform: uppercase;
    letter-spacing: 0.05em;
    padding: var(--space-3) var(--space-4);
    border-bottom: 1px solid var(--border-subtle);
  }
  td {
    padding: var(--space-3) var(--space-4);
    vertical-align: top;
  }
  .state {
    display: flex;
    align-items: center;
    gap: var(--space-2);
  }
  .name {
    color: var(--text-primary);
    font-weight: 500;
  }
  .sub {
    color: var(--text-tertiary);
    font-size: 12px;
  }
  .error {
    display: block;
    margin-top: var(--space-2);
    color: var(--color-danger);
    font-size: 12px;
  }
  .count {
    color: var(--text-tertiary);
    font-size: 12px;
  }
  .page-failure {
    padding: 0 var(--space-4) var(--space-3);
  }
  .empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--space-2);
    margin: 0;
    padding: var(--space-4) 0;
    text-align: center;
    color: var(--text-tertiary);
    font-size: 12px;
  }
  .empty p {
    margin: 0;
  }
</style>
